import React from "react";
import { useCallbackRef } from "@core/hooks/use-callback-ref";

/**
 * Returns a debounced version of a callback function.
 *
 * @description
 * Wraps a callback so that it is only invoked after `delay` milliseconds of inactivity.
 * Uses `useCallbackRef` internally to keep the callback reference stable without triggering
 * re-renders. The timeout is automatically cleaned up on unmount.
 *
 * Used internally by `useDataTable` for debouncing filter URL updates.
 *
 * @template T - The callback function type.
 * @param callback - The function to debounce.
 * @param delay - Debounce delay in milliseconds.
 * @returns A stable debounced function with the same signature as `callback`.
 *
 * @example
 * const debouncedSave = useDebouncedCallback((value: string) => {
 *   saveToServer(value);
 * }, 500);
 */
export function useDebouncedCallback<T extends (...args: never[]) => unknown>(callback: T, delay: number) {
  const handleCallback = useCallbackRef(callback);
  const debounceTimerRef = React.useRef<number>(0);
  React.useEffect(() => () => window.clearTimeout(debounceTimerRef.current), []);

  return React.useCallback(
    (...args: Parameters<T>) => {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = window.setTimeout(() => handleCallback(...args), delay);
    },
    [handleCallback, delay],
  );
}
